import React, { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Contact | Pool Guru Treasure Coast";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", city: "", message: "" });
    } catch {
      toast.error("Please check the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-gradient-to-b from-pink-50/50 to-white py-20">
        <div className="section-container">
          <div className="overline mb-3">Get In Touch</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 max-w-4xl">
            Ready for worry-free <span className="text-brand-pink">pool service?</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl">
            Request your free quote today. We'll be in touch within 24 hours with a customized service plan for your pool.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="section-container grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-8 lg:p-10 shadow-sm" data-testid="contact-form">
              <h2 className="text-2xl font-extrabold mb-6">Request a free estimate</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Name *</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email *</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone</label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">City</label>
                  <Input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="e.g. Port St. Lucie"
                    data-testid="contact-city"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Tell us about your pool *</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={5}
                  placeholder="Pool size, current issues, frequency of service, etc."
                  required
                  data-testid="contact-message"
                />
              </div>
              <Button type="submit" disabled={submitting} className="bg-brand-pink hover:bg-brand-pink-hover h-12 px-8 text-base" data-testid="contact-submit">
                {submitting ? "Sending..." : "Send Request"}
              </Button>
            </form>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <a href="tel:7722890405" className="flex items-start gap-4 bg-brand-pink text-white p-6 rounded-2xl shadow-xl shadow-pink-500/20 hover:bg-brand-pink-hover transition-colors" data-testid="contact-info-phone">
              <Phone className="w-6 h-6 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider opacity-90 font-bold">Call anytime</div>
                <div className="text-2xl font-heading font-extrabold">772-289-0405</div>
              </div>
            </a>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-start gap-4">
              <Mail className="w-5 h-5 text-brand-blue mt-1 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Email</div>
                <a href="mailto:info@thepoolgurupsl.com" className="font-bold text-brand-navy break-all">
                  info@thepoolgurupsl.com
                </a>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-start gap-4">
              <MapPin className="w-5 h-5 text-brand-blue mt-1 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Office</div>
                <div className="font-bold text-brand-navy">1961 SE Crowberry Dr</div>
                <div className="text-slate-600 text-sm">Port St. Lucie, FL</div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-start gap-4">
              <Clock className="w-5 h-5 text-brand-blue mt-1 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Hours</div>
                <div className="font-bold text-brand-navy">Mon–Fri: 9 AM – 5 PM</div>
                <div className="text-slate-600 text-sm">Emergency service available</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
